/* The guest list — STORY-016.
 *
 * An Attendance is one Person on one Event, carrying a headcount. The Person's
 * name travels by reference on every read and is never stored on the row: a
 * copied name goes stale the moment somebody corrects a spelling, and making
 * that correction possible is what the contact book is for.
 */
import * as z from 'zod';
/** The four, from `glossary.md`. STORY-017 owns moving between them. */
export const ATTENDANCE_STATUSES = ['invited', 'confirmed', 'attended', 'cancelled'];
/** The four, from `glossary.md`. Set by hand — the product processes no guest payment. */
export const PAYMENT_STATUSES = ['unpaid', 'paid', 'comped', 'refunded'];
/**
 * Above this, a Pax is probably a typo — and only probably.
 *
 * It warns and never blocks. There is no number that is definitely wrong: a
 * wedding is four figures and a festival is five. What a mistyped Pax does is
 * silently inflate every headcount downstream, so it is worth saying out loud
 * and not worth refusing.
 */
export const IMPLAUSIBLE_PAX = 50;
const pax = z
    .number()
    .int('A headcount is a whole number of people.')
    .min(1, 'A headcount starts at one.');
export const attendanceSchema = z.object({
    id: z.string(),
    /** By reference, resolved on every read. Never copied onto the row. */
    person: z.object({
        id: z.string(),
        displayName: z.string(),
    }),
    status: z.enum(ATTENDANCE_STATUSES),
    paymentStatus: z.enum(PAYMENT_STATUSES),
    pax: z.number().int(),
    createdAt: z.string(),
});
/**
 * The statuses whose Pax counts toward the Event's headcount.
 *
 * **Both, and that is the point.** Somebody who confirmed and somebody who
 * turned up are equally a head to cater for, so an Event mid-transition does
 * not have its headcount collapse as rows move between the two.
 *
 * `invited` is excluded because they have not replied, and `cancelled`
 * because they dropped out — but both stay VISIBLE on the guest list, so a
 * Member can see who has not answered and who pulled out.
 */
export const COUNTED_ATTENDANCE_STATUSES = [
    'confirmed',
    'attended',
];
/** Ten at a time. Decided from the prototype, 2026-08-14. */
export const GUESTS_PAGE_SIZE = 10;
/**
 * The page sizes offered, and the ceiling.
 *
 * "All" is the ceiling rather than a genuinely unbounded read: a request that
 * can return any number of rows is a request that can be made to return all of
 * them. The totals always describe the whole list, so a list longer than this
 * still SAYS so — "Showing 1–500 of 620" is a truthful screen, where an
 * unbounded query is an unbounded query.
 */
export const GUESTS_PAGE_SIZES = [10, 20, 50, 100];
export const GUESTS_PAGE_MAX = 500;
export const listGuestsQuerySchema = z.object({
    limit: z.number().int().min(1).max(GUESTS_PAGE_MAX).optional(),
    offset: z.number().int().min(0).optional(),
});
export const guestListResponseSchema = z.object({
    attendances: z.array(attendanceSchema),
    /**
     * Two numbers, because they answer different questions.
     *
     * `guests` is how many invitations there are — how many people to write to.
     * `pax` is how many covers they add up to — how many to cook for. A list of
     * forty Attendances can easily be sixty-something people, and conflating them
     * is how a kitchen gets the wrong number.
     *
     * **Both count the whole guest list, never the page.** Paging is a view
     * concern, and a total that changed when somebody clicked "next" would be a
     * defect — STORY-017 says so outright.
     *
     * `pax` counts `COUNTED_ATTENDANCE_STATUSES` only. It is derived on every
     * read and no column holds it: a stored copy is how a headcount and the rows
     * under it come to disagree.
     */
    guests: z.number().int(),
    pax: z.number().int(),
    /**
     * How many Attendances sit at each payment status, over the whole list.
     *
     * Every status is present, including the ones at zero: a strip that hides
     * "refunded" until somebody is refunded is a strip whose shape changes under
     * you, and a zero is an answer. Counts, never amounts.
     */
    payments: z.object({
        unpaid: z.number().int(),
        paid: z.number().int(),
        comped: z.number().int(),
        refunded: z.number().int(),
    }),
});
export const addAttendanceRequestSchema = z.object({
    personId: z.string().uuid(),
    /* Optional, defaulting to one, because most of them are one. */
    pax: pax.optional(),
});
/**
 * Pax and status. Payment is STORY-018.
 *
 * Absent leaves a field alone. Both are optional and independent, so changing a
 * headcount on the night does not also assert that somebody turned up.
 */
export const updateAttendanceRequestSchema = z.object({
    pax: pax.optional(),
    /**
     * Any status, from any status.
     *
     * Free in both directions, and deliberately: a cancelled guest who reappears
     * goes back to `confirmed` without ceremony. Strict lifecycle rules here
     * would fight how teams actually work — the same decision the Event lifecycle
     * made in STORY-012, for the same reason.
     */
    status: z.enum(ATTENDANCE_STATUSES).optional(),
    /**
     * Any payment status, from any payment status.
     *
     * **Independent of the Attendance status in both directions** — STORY-018.
     * A cancelled guest may still be owed a refund, and one who attended may
     * still owe. Linking them would force a lie in one direction or the other.
     *
     * No amount travels with it. Payment status is a flag; the money is a
     * Transaction (EPIC-004), and two places holding the same figure is how the
     * source's totals stopped agreeing with each other.
     */
    paymentStatus: z.enum(PAYMENT_STATUSES).optional(),
});
//# sourceMappingURL=attendances.js.map